import Link from '@src/components/base/Link'
import { GRAMMAR } from '@src/components/Cards/constants'
import { ListItem, UnorderedList } from '@src/components/List'
import Spinner from '@src/components/Spinner'
import { getCheckCategoryPath } from '@src/layouts/shared/NavTabs/utils'
import { PAGE_TYPES } from '@src/state/analytics/constants'
import { CheckCategory } from '@src/state/document/types'
import { deletePaper, DeletePaperPayload, getPapers } from '@src/state/papers/actions'
import { Paper } from '@src/state/papers/reducer'
import { StoreState } from '@src/state/reducers'
import { isDemoSelector } from '@src/state/router/selectors'
import { setNewUpload } from '@src/state/workspace/actions'
import { selectInitialCheckCategory } from '@src/state/workspace/selectors'
import media from '@src/styled/media'
import easybib from '@src/styled/theme'
import {
  DELETE_PAPER_BUTTON,
  MY_FIRST_PAPER_LINK,
  PAPERS_LOADING_UNRESOLVED,
} from '@src/utils/dataTestIds'
import toMonthDayYearString from '@src/utils/Date'
import * as React from 'react'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import styled, { css } from 'styled-components'
import DeleteIcon from './DeleteIcon'

interface DispatchProps {
  getPapers: () => void
  deletePaper: (payload: DeletePaperPayload) => void
  setNewUpload: () => void
}

interface PapersProps {
  papers: Paper[]
  resolved: boolean
  checkCategory: CheckCategory
  isDemo: boolean
}

interface PaperRowProps {
  paper: Paper
  deletePaper: (payload: DeletePaperPayload) => void
  index: number
  checkCategory: CheckCategory
  setNewUpload: () => void
  isDemo: boolean
}

interface PapersTableProps extends DispatchProps, PapersProps {}

interface FlexRowProps {
  borderPx?: number
}

const Container = styled.div`
  max-width: 855px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.md};
`

const FlexRow = styled.div<FlexRowProps>`
  display: flex;
  color: ${({ theme }) => theme.colors.grey};
  width: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.colors.greyLight};
  align-items: center;
  height: 45px;
`

const FirstColumnChild = styled.div`
  max-width: 512px;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 3;
`

const ThirdColumnChild = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  flex: 1;
`

const SecondColumnChild = styled(ThirdColumnChild)`
  flex: 1;
  justify-content: flex-start;
  ${media.phone`
    display: none;
  `};
`

const TitleText = styled.span`
  line-height: 25px;
  font-weight: 700;
  font-size: 18px;
  color: ${({ theme }) => theme.colors.dark};
`

const childFontCss = css`
  font-size: 12px;
  font-weight: 100;
`

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 200px;
`

const NameLink = styled(Link)`
  color: ${({ theme }) => theme.colors.link} ${childFontCss};
`
const DateText = styled.span`
  ${childFontCss};
  color: ${({ theme }) => theme.colors.dark};
`

const Heading = styled.h1`
  color: ${({ theme }) => theme.colors.dark};
  text-align: center;
  font-size: 34px;
  margin-block-start: 1em;
  margin-block-end: 1em;
`

const CopyRow = styled.div`
  text-align: center;
  height: 45px;
`

const LoadingView = () => (
  <SpinnerContainer data-test={PAPERS_LOADING_UNRESOLVED}>
    <Spinner color={easybib.colors.cheggOrange} />
  </SpinnerContainer>
)

const PaperRow: React.FunctionComponent<PaperRowProps> = props => {
  const { paper, index, checkCategory, setNewUpload, isDemo } = props
  const payload = { id: paper.id } as DeletePaperPayload
  const onDelete = () => props.deletePaper(payload)
  const to = {
    pathname: getCheckCategoryPath({ paperId: paper.id, checkCategory, isDemo }),
    state: { from: PAGE_TYPES.PAPERS },
  }
  const firstPaperTestId = index === 0 ? MY_FIRST_PAPER_LINK : null
  return (
    <ListItem key={paper.id} role="row">
      <FlexRow>
        <FirstColumnChild onClick={setNewUpload} role="cell">
          <NameLink title={paper.filename} data-test={firstPaperTestId} to={to}>
            {paper.filename}
          </NameLink>
        </FirstColumnChild>
        <SecondColumnChild role="cell">
          <DateText>{toMonthDayYearString(paper.updatedAt)}</DateText>
        </SecondColumnChild>
        <ThirdColumnChild>
          <DeleteIcon
            onClick={onDelete}
            data-test={DELETE_PAPER_BUTTON}
            filename={paper.filename}
            aria-label={`${paper.filename} delete paper button`}
          />
        </ThirdColumnChild>
      </FlexRow>
    </ListItem>
  )
}

export const NO_PAPERS_COPY = `You don't have any papers yet. Upload your first paper above!`
const NoPapers = () => (
  <CopyRow>
    <span>{NO_PAPERS_COPY}</span>
  </CopyRow>
)

const Papers: React.FunctionComponent<PapersTableProps> = props => (
  <UnorderedList role="rowgroup">
    {props.papers.map((upload: Paper, index: number) => (
      <PaperRow
        checkCategory={props.checkCategory}
        deletePaper={props.deletePaper}
        paper={upload}
        key={upload.id}
        index={index}
        setNewUpload={props.setNewUpload}
        isDemo={props.isDemo}
      />
    ))}
  </UnorderedList>
)

const Header: React.FunctionComponent = () => (
  <FlexRow role="row">
    <FirstColumnChild role="columnheader">
      <TitleText>Title</TitleText>
    </FirstColumnChild>
    <SecondColumnChild role="columnheader">
      <TitleText>Last Accessed</TitleText>
    </SecondColumnChild>
    <ThirdColumnChild />
  </FlexRow>
)

export class PapersTable extends React.Component<PapersTableProps> {
  public componentDidMount() {
    this.props.getPapers()
  }

  public render() {
    const { papers, resolved } = this.props

    if (!papers.length && !resolved) {
      return <LoadingView />
    }

    return (
      <Container role="table" aria-describedby="papers-heading">
        <Heading id="papers-heading">My papers</Heading>
        {papers.length ? (
          <React.Fragment>
            <Header />
            <Papers {...this.props} />
          </React.Fragment>
        ) : (
          <NoPapers />
        )}
      </Container>
    )
  }
}

const mapStateToProps = (state: StoreState) => ({
  papers: state.papers.papers,
  resolved: state.papers.resolved,
  checkCategory: selectInitialCheckCategory(state) || GRAMMAR,
  isDemo: isDemoSelector(state),
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getPapers: () => dispatch(getPapers()),
  deletePaper: (payload: DeletePaperPayload) => dispatch(deletePaper.request(payload)),
  setNewUpload: () => dispatch(setNewUpload({ isNew: false })),
})

export default connect<PapersProps, DispatchProps>(
  mapStateToProps,
  mapDispatchToProps
)(PapersTable)



// WEBPACK FOOTER //
// ./src/pages/MyPapers/PapersTable.tsx